import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  RefreshCw, 
  Search,
  Filter,
  User,
  Building,
  Phone,
  Mail,
  Calendar,
  Target,
  Eye,
  ExternalLink,
  Sparkles,
  List,
  Grid,
  Download,
  MoreVertical,
  ChevronDown,
  Tag,
  X
} from 'lucide-react';
import LeadForm from './LeadForm';
import LeadDetail from './LeadDetail';
import DynamicEntityTable from '../../components/EntityManagement/DynamicEntityTable';
import SpreadsheetView from '../../components/EntityManagement/SpreadsheetView';
import EntityFilter from '../../components/EntityManagement/EntityFilter';
import BulkTagManager from '../../components/TagManagement/BulkTagManager';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { api } from '../../utils/api';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { clearConfigCache } from '../../utils/entityConfig';
import { getFieldOptionsSync, isSelectField as checkIsSelectField } from '../../hooks/useFieldOptions';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import DependencyDeleteDialog from '../../components/UI/DependencyDeleteDialog';
import { toast } from 'react-hot-toast';

const Leads = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'leads.title',
    'leads.addLead',
    'leads.searchPlaceholder',
    'leads.status',
    'leads.score'
  ]);
  const location = useLocation();
  const navigate = useNavigate();
  const { getProtectedTenantCode } = useTenantProtection();
  const { hasRole } = useAuth();
  
  // Load entity configuration with proper caching and loading states
  const { config: entityConfig, loading: configLoading, error: configError } = useEntityConfig('lead');
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedLead, setSelectedLead] = useState(null);
  // Removed stats summary UI
  const [focusedLeadId, setFocusedLeadId] = useState(null);
  const [hasScrolledToLead, setHasScrolledToLead] = useState(false);
  const leadRefs = useRef({});
  // Abort in-flight requests when user navigates away (releases backend pool connections)
  const abortControllerRef = useRef(null);

  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    qualificationLevel: '',
    industry: '',
    companySize: '',
    minScore: '',
    maxScore: ''
  });
  
  // Advanced filters for EntityFilter component
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const activeAdvancedFilters = useMemo(() => (
    advancedFilters.filter(f =>
      f.field &&
      f.operator &&
      (f.operator === 'isNull' ||
        f.operator === 'isNotNull' ||
        (f.value !== undefined && f.value !== ''))
    )
  ), [advancedFilters]);
  const activeFiltersCount = useMemo(() => {
    const simpleCount = Object.values(filters).filter(value => value !== '').length;
    const tagCount = selectedTag ? 1 : 0;
    return simpleCount + tagCount + activeAdvancedFilters.length;
  }, [filters, selectedTag, activeAdvancedFilters]);
  const hasSimpleFilters = useMemo(
    () => Object.values(filters).some(value => value !== '') || Boolean(selectedTag),
    [filters, selectedTag]
  );
  const hasActiveFilters = activeFiltersCount > 0;
  
  // Dynamic list values for filters
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [qualificationLevelOptions, setQualificationLevelOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  
  // Entity configuration
  const [hiddenFields, setHiddenFields] = useState([]);
  
  // View mode and sorting
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'spreadsheet'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Spreadsheet selection
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk actions
  const [showBulkTagManager, setShowBulkTagManager] = useState(false);
  
  // Filter and menu visibility
  const [showFilters, setShowFilters] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  
  // Salesperson filter state (for Admin/Manager)
  const [salespersonFilter, setSalespersonFilter] = useState('all');
  const [salespersons, setSalespersons] = useState([]);
  const [showSalespersonFilter, setShowSalespersonFilter] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Load entity configuration including hidden fields
  const loadEntityConfig = useCallback(async () => {
    if (!entityConfig) return; // Wait for entity config to load
    
    try {
      console.log('🔍 Leads: Loading entity configuration...');
      // The entityConfig returned from useEntityConfig hook is already the config object
      
      // Load hidden fields from config
      // The entityConfig returned from useEntityConfig hook already has hiddenFields at root level
      const configHiddenFields = entityConfig.hiddenFields || [];
      if (Array.isArray(configHiddenFields)) {
        setHiddenFields(configHiddenFields);
        console.log('✅ Leads: Loaded hidden fields from config:', configHiddenFields);
      } else {
        console.warn('⚠️ Leads: hiddenFields is not an array:', configHiddenFields);
        setHiddenFields([]);
      }
    } catch (error) {
      console.error('❌ Leads: Error loading entity config:', error);
    }
  }, [entityConfig]);

  // Load selection values from tenant config with global fallback
  const loadSelectionValues = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      console.log('🔍 Leads: Loading selection values from tenant config...');
      const response = await fetch('/api/tenant-selection/lead', {
        signal,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const selectionValues = data.selectionValues;
        
        console.log('🔍 Leads: Received selection values:', selectionValues);
        
        // Update Priority options
        if (selectionValues?.Priority?.values) {
          const priorityOpts = selectionValues.Priority.values.map(v => ({
            value: v.value,
            label: v.label
          }));
          setPriorityOptions(priorityOpts);
          console.log('✅ Leads: Set Priority options:', priorityOpts);
        }
        
        // Update Status options
        if (selectionValues?.Status?.values) {
          const statusOpts = selectionValues.Status.values.map(v => ({
            value: v.value,
            label: v.label
          }));
          setStatusOptions(statusOpts);
          console.log('✅ Leads: Set Status options:', statusOpts);
        }
        
        // Update QualificationLevel options
        if (selectionValues?.QualificationLevel?.values) {
          const qualOpts = selectionValues.QualificationLevel.values.map(v => ({
            value: v.value,
            label: v.label
          }));
          setQualificationLevelOptions(qualOpts);
          console.log('✅ Leads: Set QualificationLevel options:', qualOpts);
        }
        
        // Update LeadSource options
        if (selectionValues?.LeadSource?.values) {
          const sourceOpts = selectionValues.LeadSource.values.map(v => ({
            value: v.value,
            label: v.label
          }));
          setLeadSourceOptions(sourceOpts);
          console.log('✅ Leads: Set LeadSource options:', sourceOpts);
        }
      } else {
        console.error('❌ Leads: Failed to fetch selection values, status:', response.status);
        // Fallback to default values
        setPriorityOptions([
          { value: 'Low', label: 'Low' },
          { value: 'Medium', label: 'Medium' },
          { value: 'High', label: 'High' },
          { value: 'Critical', label: 'Critical' },
        ]);
        setStatusOptions([
          { value: 'New', label: 'New' },
          { value: 'Contacted', label: 'Contacted' },
          { value: 'Qualified', label: 'Qualified' },
          { value: 'Unqualified', label: 'Unqualified' },
          { value: 'Converted', label: 'Converted' },
        ]);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('❌ Leads: Error loading selection values:', error);
      // Fallback to default values
      setPriorityOptions([
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' },
        { value: 'Critical', label: 'Critical' },
      ]);
      setStatusOptions([
        { value: 'New', label: 'New' },
        { value: 'Contacted', label: 'Contacted' },
        { value: 'Qualified', label: 'Qualified' },
        { value: 'Unqualified', label: 'Unqualified' },
        { value: 'Converted', label: 'Converted' },
      ]);
    }
  };

  const fetchAvailableTags = async () => {
    const signal = abortControllerRef.current?.signal;
    try {
      setTagsLoading(true);
      const response = await fetch('/api/tags?entityType=Lead', {
        signal,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data.tags || []);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Failed to fetch tags:', error);
    } finally {
      setTagsLoading(false);
    }
  };

  // Load tags only when user opens the tag filter dropdown (reduces initial page load)
  const handleTagDropdownFocus = () => {
    if (availableTags.length === 0 && !tagsLoading) {
      fetchAvailableTags();
    }
  };

  const fetchLeads = async (resetPage = false) => {
    const signal = abortControllerRef.current?.signal;
    try {
      setLoading(true);
      const page = resetPage ? 1 : pagination.page;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });
      
      // Add advanced filters if any
      const validFilters = advancedFilters.filter(f => 
        f.field && f.operator && (f.operator === 'isNull' || f.operator === 'isNotNull' || (f.value !== undefined && f.value !== ''))
      );
      if (validFilters.length > 0) {
        queryParams.set('filters', JSON.stringify(validFilters));
      } else {
        // Remove filters parameter if no valid filters
        queryParams.delete('filters');
      }
      
      if (selectedTag) {
        queryParams.set('tag', selectedTag);
      }

      // Add salesperson filter if set (Admin/Manager only)
      if (salespersonFilter && salespersonFilter !== 'all') {
        queryParams.set('salespersonId', salespersonFilter);
      }
      
      const data = await api.get(`/leads?${queryParams}`, { signal, cacheTtlMs: 3000 });
      
      if (signal?.aborted) return;
      setLeads(data.leads || []);
      // Fix: Use data.pagination directly instead of fallback to current pagination
      if (data.pagination) {
        setPagination(data.pagination);
      }
      setError(null);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // Stats fetch removed

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    clearConfigCache('lead');
    loadEntityConfig();
    loadSelectionValues();
    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, []); // Initial load only - no dependencies to prevent loops (tags load on dropdown open)

  const filtersKey = useMemo(() => JSON.stringify(advancedFilters), [advancedFilters]);
  const listDepsRef = useRef({ filtersKey, salespersonFilter, selectedTag, filters });

  useEffect(() => {
    const filterRelatedChanged =
      listDepsRef.current.filtersKey !== filtersKey ||
      listDepsRef.current.salespersonFilter !== salespersonFilter ||
      listDepsRef.current.selectedTag !== selectedTag ||
      listDepsRef.current.filters !== filters;

    listDepsRef.current = { filtersKey, salespersonFilter, selectedTag, filters };

    if (filterRelatedChanged && pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      return;
    }

    fetchLeads(filterRelatedChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filtersKey, salespersonFilter, selectedTag, filters]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [hasActiveFilters]);

  // Tags are loaded on demand when user opens the tag dropdown (handleTagDropdownFocus)

  // Fetch salespersons for filter (Admin/Manager only)
  useEffect(() => {
    const fetchSalespersons = async () => {
      if (!hasRole(['Admin', 'Manager'])) return;
      
      try {
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch('/api/users/salespersons', {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          // Filter to only include users with Salesperson role
          const salespersonOnly = (data.salespersons || []).filter(user => {
            const roles = user.Roles || '';
            return roles.includes('Salesperson');
          });
          setSalespersons(salespersonOnly);
        }
      } catch (error) {
        console.error('Error fetching salespersons:', error);
      }
    };
    
    fetchSalespersons();
  }, [hasRole]);

  // Close salesperson filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSalespersonFilter && !event.target.closest('.salesperson-filter-container')) {
        setShowSalespersonFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSalespersonFilter]);

  // Handle leadId query parameter for scrolling to specific lead
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const leadId = urlParams.get('leadId');
    
    if (leadId && !hasScrolledToLead && leads.length > 0) {
      setFocusedLeadId(parseInt(leadId));
      
      // Wait a bit for the DOM to render, then scroll to the lead
      setTimeout(() => {
        const leadRef = leadRefs.current[leadId];
        if (leadRef) {
          leadRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          setHasScrolledToLead(true);
          
          // Add a temporary highlight effect
          leadRef.style.backgroundColor = '#fef3c7';
          leadRef.style.transition = 'background-color 0.3s ease';
          setTimeout(() => {
            leadRef.style.backgroundColor = '';
          }, 2000);
        }
      }, 500);
    }
  }, [leads, location.search, hasScrolledToLead]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      qualificationLevel: '',
      industry: '',
      companySize: '',
      minScore: '',
      maxScore: ''
    });
    setSelectedTag('');
    setTimeout(() => fetchLeads(true), 100);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setFormMode('create');
    setShowForm(true);
  };

  const handleEditLead = async (lead) => {
    try {
      setLoading(true);
      const data = await api.get(`/leads/${lead.LeadID}`);
      setSelectedLead(data.lead);
      setFormMode('edit');
      setShowForm(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLead = async (lead) => {
    try {
      setLoading(true);
      const data = await api.get(`/leads/${lead.LeadID}`);
      setSelectedLead(data.lead);
      setShowDetail(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    leadId: null,
    dependencies: null
  });

  const handleDeleteLead = async (leadId, confirmDeleteDependencies = false) => {
    // If not confirming dependencies, show initial confirmation
    if (!confirmDeleteDependencies) {
      if (!window.confirm(safeT('common.confirmDelete', 'Are you sure you want to delete this item?'))) return;
    }
    
    try {
      // First, delete all attachments associated with this lead
      try {
        console.log('🔍 Deleting attachments for lead:', leadId);
        const attachmentResponse = await api.delete(`/attachments/Lead/${leadId}/all`);
        console.log('✅ Attachments deleted:', attachmentResponse);
        
        if (attachmentResponse.deletedCount > 0) {
          console.log(`✅ Deleted ${attachmentResponse.deletedCount} attachments for lead ${leadId}`);
        }
      } catch (attachmentError) {
        console.warn('⚠️ Failed to delete attachments for lead:', attachmentError);
        // Continue with lead deletion even if attachment deletion fails
      }
      
      // Then delete the lead itself - use fetch directly to handle 409 status
      const selectedRole = localStorage.getItem('selectedRole');
      const url = `/api/leads/${leadId}${confirmDeleteDependencies ? '?confirmDeleteDependencies=true' : ''}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {}),
          ...api.getAuthHeaders()
        }
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Check if this is a dependency confirmation required response
        if (response.status === 409 && responseData.requiresConfirmation) {
          setDeleteDialog({
            isOpen: true,
            leadId: leadId,
            dependencies: responseData.dependencies
          });
          return;
        }
        throw new Error(responseData.message || 'Failed to delete lead');
      }

      fetchLeads();
      setError(null);
      setDeleteDialog({ isOpen: false, leadId: null, dependencies: null });
      
      if (responseData.deletedDependencies) {
        toast.success(`Lead deleted successfully. Also deleted: ${responseData.deletedDependencies.join(', ')}`);
      } else {
        toast.success('Lead deleted successfully');
      }
    } catch (err) {
      setError(err.message);
      setDeleteDialog({ isOpen: false, leadId: null, dependencies: null });
    }
  };

  const handleConfirmBulkDeleteWithDependencies = () => {
    if (deleteDialog.isBulk && deleteDialog.entityIds && Array.isArray(deleteDialog.entityIds)) {
      // Restore selected leads from dialog if available
      const sanitizedIds = deleteDialog.entityIds
        .map(id => {
          const numId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
          return isNaN(numId) ? null : numId;
        })
        .filter(id => id !== null && id !== undefined && typeof id === 'number');
      
      if (sanitizedIds.length > 0) {
        setSelectedLeads(new Set(sanitizedIds));
      }
      handleBulkDelete(true);
    }
  };

  const handleConfirmDeleteWithDependencies = () => {
    if (deleteDialog.isBulk) {
      handleConfirmBulkDeleteWithDependencies();
    } else if (deleteDialog.leadId) {
      handleDeleteLead(deleteDialog.leadId, true);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (formMode === 'create') {
        await api.post('/leads', formData);
      } else {
        await api.put(`/leads/${selectedLead.LeadID}`, formData);
      }
      setShowForm(false);
      fetchLeads();
    } catch (err) {
      setError(err.message);
    }
  };

  // Spreadsheet handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads(new Set());
      setSelectAll(false);
    } else {
      // Ensure we only store numeric IDs
      const allIds = leads
        .map(lead => {
          const id = lead?.LeadID;
          return id != null ? Number(id) : null;
        })
        .filter(id => id !== null && !isNaN(id));
      setSelectedLeads(new Set(allIds));
      setSelectAll(true);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async (confirmDeleteDependencies = false) => {
    if (selectedLeads.size === 0) {
      toast.error('Please select at least one lead to delete');
      return;
    }

    // If not confirming dependencies, show initial confirmation
    if (!confirmDeleteDependencies) {
      const count = selectedLeads.size;
      if (!window.confirm(
        `Are you sure you want to delete ${count} lead${count !== 1 ? 's' : ''}? This action cannot be undone.`
      )) {
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      // Sanitize leadIds - ensure they are all numbers and filter out any invalid values
      const leadIds = Array.from(selectedLeads)
        .map(id => {
          // Convert to number if it's a string, or return as-is if already a number
          const numId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
          return isNaN(numId) ? null : numId;
        })
        .filter(id => id !== null && id !== undefined && typeof id === 'number');
      
      if (leadIds.length === 0) {
        toast.error('No valid lead IDs selected');
        setLoading(false);
        return;
      }
      
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch('/api/leads/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        },
        body: JSON.stringify({ 
          leadIds,
          confirmDeleteDependencies 
        })
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Check if this is a dependency confirmation required response
        if (response.status === 409 && responseData.requiresConfirmation) {
          // Ensure entityIds are sanitized numbers
          const sanitizedEntityIds = leadIds.filter(id => typeof id === 'number' && !isNaN(id));
          setDeleteDialog({
            isOpen: true,
            leadId: null, // Use null to indicate bulk delete
            dependencies: responseData.dependencies,
            isBulk: true,
            entityIds: sanitizedEntityIds
          });
          setLoading(false);
          return;
        }
        throw new Error(responseData.message || 'Failed to delete leads');
      }

      const result = responseData;
      
      // Clear selection
      setSelectedLeads(new Set());
      setSelectAll(false);
      
      // Refresh leads
      await fetchLeads();
      setDeleteDialog({ isOpen: false, leadId: null, dependencies: null });
      
      toast.success(
        `Successfully deleted ${result.deletedCount || leadIds.length} lead${result.deletedCount !== 1 ? 's' : ''}`
      );
    } catch (err) {
      setError(err.message);
      setDeleteDialog({ isOpen: false, leadId: null, dependencies: null });
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk tag manager success
  const handleBulkTagSuccess = () => {
    // Refresh the lead list to show updated tags
    fetchLeads();
  };

  const handleCellSave = async (leadId, fieldName, newValue) => {
    try {
      // Build update object - for custom attributes, ensure proper structure
      const updateData = {};
      
      if (fieldName.startsWith('attr_')) {
        // Custom attribute
        const attributeName = fieldName.substring(5); // Remove 'attr_' prefix
        updateData.attributes = { [attributeName]: newValue };
      } else {
        // Regular field
        updateData[fieldName] = newValue;
      }
      
      await api.put(`/leads/${leadId}`, updateData);
      
      // Refresh leads to show updated data
      await fetchLeads();
      
      return true;
    } catch (error) {
      console.error('Error saving lead cell:', error);
      throw error;
    }
  };

  const getPriorityColor = (priority) => {
    // Find the priority option to get its color
    const priorityOption = priorityOptions.find(opt => opt.value === priority);
    if (priorityOption && priorityOption.color) {
      const colorMap = {
        'red': 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
        'orange': 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
        'green': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        'gray': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'blue': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        'purple': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      };
      return colorMap[priorityOption.color] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
    
    // Fallback to original logic for backward compatibility
    switch (priority) {
      case 'Critical': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'High': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'Medium': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'Low': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (status) => {
    // Find the status option to get its color
    const statusOption = statusOptions.find(opt => opt.value === status);
    if (statusOption && statusOption.color) {
      const colorMap = {
        'red': 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
        'orange': 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
        'yellow': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
        'green': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        'gray': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        'blue': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        'purple': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
      };
      return colorMap[statusOption.color] || 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
    }
    
    // Fallback to default blue for status
    return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Helper function to check if field is hidden
  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };

  // Helper function to format currency (now using utility)
  const formatCurrencyLocal = (amount, currency = 'THB') => {
    return formatCurrency(amount, currency);
  };

  // Spreadsheet view functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Build spreadsheet columns
  const spreadsheetColumns = useMemo(() => {
    if (!entityConfig || !entityConfig.fields) return [];
    
    // Import isFieldHidden helper
    const isFieldHidden = (config, fieldName, field) => {
      if (!config || !field) return false;
      if (field._tenantHidden === true) return true;
      const hiddenFields = config.hiddenFields || config.entity?.hiddenFields || [];
      if (Array.isArray(hiddenFields) && hiddenFields.includes(fieldName)) return true;
      return false;
    };
    
    // Get fields that should show in list
    const fields = Object.keys(entityConfig.fields)
      .map(fieldName => {
        const field = entityConfig.fields[fieldName];
        if (!field?.ui?.showInList) return null;
        // Check if field is hidden
        if (isFieldHidden(entityConfig, fieldName, field)) return null;
        return {
          name: fieldName,
          ...field,
          order: field.ui.order || 999
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    
    return fields.map(field => ({
      key: field.name,
      label: field.displayName || field.name,
      width: '150px',
      editable: field.database?.table === 'Leads', // Only direct table columns editable
      fieldName: field.name,
      fieldConfig: field
    }));
  }, [entityConfig]);

  const getSpreadsheetCellValue = (lead, columnKey, fieldConfig) => {
    const rawValue = lead[columnKey];
    
    // Handle SalespersonID - show name instead of ID
    if (columnKey === 'SalespersonID') {
      return lead.SalespersonName || '-';
    }
    
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }
    
    // Check if this is a select field and get label
    if (fieldConfig && checkIsSelectField(entityConfig, columnKey)) {
      const fieldOptions = getFieldOptionsSync(entityConfig, columnKey);
      const option = fieldOptions.find(opt => opt.value === rawValue);
      return option ? (option.label || option.value) : rawValue;
    }
    
    // Format dates
    if (fieldConfig?.dataType === 'date' && rawValue) {
      try {
        return new Date(rawValue).toLocaleDateString();
      } catch {
        return rawValue;
      }
    }
    
    if (fieldConfig?.dataType === 'datetime' && rawValue) {
      try {
        return new Date(rawValue).toLocaleString();
      } catch {
        return rawValue;
      }
    }
    
    // Format currency
    if (columnKey === 'EstimatedBudget' && rawValue) {
      return formatCurrencyLocal(rawValue, lead.Currency);
    }
    
    return String(rawValue);
  };

  const isFieldEditable = (lead, columnKey, fieldConfig) => {
    if (!fieldConfig) return null;
    
    const dbTable = fieldConfig.database?.table;
    
    // Only fields from Leads table are editable
    if (dbTable === 'Leads') {
      return columnKey;
    }
    
    // Custom attributes are always editable
    if (fieldConfig.storage?.type === 'attributes') {
      return columnKey;
    }
    
    return null;
  };

  // Custom render functions for DynamicEntityTable
  const customRender = useMemo(() => ({
    Title: (lead) => (
      <div className="flex items-center">
        <div className="flex-1">
          <div className="text-xs sm:text-sm md:text-base font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-150">
            {lead.Title || 'N/A'}
          </div>
          {lead.Description && (
            <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
              {lead.Description}
            </div>
          )}
        </div>
        <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ml-2" />
      </div>
    ),
    SalespersonID: (lead) => (
      <div className="flex items-center space-x-2">
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <span className="text-sm text-gray-900 dark:text-white">
          {lead.SalespersonName || 'N/A'}
        </span>
      </div>
    ),
    ContactName: (lead) => (
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10">
          <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
            <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
        <div className="ml-4">
          <div className="text-xs sm:text-sm md:text-base font-medium text-gray-900 dark:text-white">
            {lead.ContactName || 'N/A'}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
            {lead.ContactEmail && (
              <span className="flex items-center">
                <Mail className="h-3 w-3 mr-1" />
                {lead.ContactEmail}
              </span>
            )}
            {lead.ContactPhone && (
              <span className="flex items-center">
                <Phone className="h-3 w-3 mr-1" />
                {lead.ContactPhone}
              </span>
            )}
          </div>
        </div>
      </div>
    ),
    CompanyName: (lead) => (
      <div>
        <div className="text-base text-gray-900 dark:text-white">{lead.CompanyName || '-'}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{lead.Industry || '-'}</div>
      </div>
    ),
    Status: (lead) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.Status)}`}>
        {lead.Status}
      </span>
    ),
    Priority: (lead) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(lead.Priority)}`}>
        {lead.Priority}
      </span>
    ),
    Score: (lead) => (
      <div className="flex items-center">
        <span className={`text-base font-medium ${getScoreColor(lead.Score)}`}>
          {lead.Score || '-'}
        </span>
        {lead.Score && (
          <div className="ml-2 w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div 
              className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full" 
              style={{ width: `${lead.Score}%` }}
            ></div>
          </div>
        )}
      </div>
    ),
    EstimatedBudget: (lead) => (
      <span className="text-base text-gray-900 dark:text-white">
        {formatCurrencyLocal(lead.EstimatedBudget, lead.Currency)}
      </span>
    ),
    NextFollowUpDate: (lead) => (
      lead.NextFollowUpDate ? (
        <div className="flex items-center text-base text-gray-500 dark:text-gray-400">
          <Calendar className="h-4 w-4 mr-1" />
          {new Date(lead.NextFollowUpDate).toLocaleDateString()}
        </div>
      ) : '-'
    )
  }), [priorityOptions, statusOptions]);

  if (showForm) {
    return (
      <LeadForm 
        lead={selectedLead} 
        mode={formMode} 
        onSubmit={handleFormSubmit} 
        onCancel={() => setShowForm(false)}
        hiddenFields={hiddenFields}
      />
    );
  }

  if (showDetail) {
    return (
      <LeadDetail 
        lead={selectedLead} 
        onEdit={() => {
          setShowDetail(false);
          setFormMode('edit');
          setShowForm(true);
        }}
        onClose={() => setShowDetail(false)}
        hiddenFields={hiddenFields}
      />
    );
  }

  const showTranslationLoading = !translationsReady || !ready;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {showTranslationLoading && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Loading translations...
        </div>
      )}
      {/* Header - Title and Actions in Same Line */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="icon/lead.png" 
              alt="Leads Icon" 
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{safeT('nav.leads', 'Leads')}</h1>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">{safeT('leads.subtitle', 'Manage and track your sales leads')}</p>
        </div>
        
        {/* Action Buttons - Collapsible Menu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
          {/* View Mode Toggle - Always Visible */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode('spreadsheet')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'spreadsheet'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Spreadsheet View"
            >
              <Grid className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Spreadsheet</span>
            </button>
          </div>
          
          {/* Filter Button - Between View Toggle and Action Buttons */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
              hasActiveFilters
                ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                : showFilters
                ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            title="Toggle filters"
          >
            <Filter className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
            {hasActiveFilters && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
          
          {/* Salesperson Filter (Admin/Manager only) */}
          {hasRole(['Admin', 'Manager']) && salespersons.length > 0 && (
            <div className="relative salesperson-filter-container">
              <button
                onClick={() => setShowSalespersonFilter(!showSalespersonFilter)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-xs sm:text-sm font-medium ${
                  salespersonFilter !== 'all'
                    ? 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
                title={safeT('leads.filterBySalesperson', 'Filter by salesperson')}
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline truncate max-w-32">
                  {salespersonFilter === 'all' 
                    ? safeT('leads.allSalespersons', 'All Salespersons')
                    : (() => {
                        const selected = salespersons.find(s => String(s.UserID) === String(salespersonFilter));
                        return selected ? `${selected.FirstName} ${selected.LastName}` : safeT('leads.allSalespersons', 'All Salespersons');
                      })()
                  }
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showSalespersonFilter && (
                <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-56 max-h-72 overflow-y-auto">
                  <div className="p-2 space-y-1">
                    <button
                      onClick={() => {
                        setSalespersonFilter('all');
                        setShowSalespersonFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        salespersonFilter === 'all'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      {safeT('leads.allSalespersons', 'All Salespersons')}
                    </button>
                    {salespersons.map(salesperson => (
                      <button
                        key={salesperson.UserID}
                        onClick={() => {
                          setSalespersonFilter(String(salesperson.UserID));
                          setShowSalespersonFilter(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                          String(salespersonFilter) === String(salesperson.UserID)
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-medium">
                          {salesperson.FirstName?.charAt(0)}{salesperson.LastName?.charAt(0)}
                        </div>
                        <span className="truncate">{salesperson.FirstName} {salesperson.LastName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Expandable Menu Buttons - Always visible on mobile, collapsible on desktop */}
            <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'sm:flex' : 'sm:hidden'}`}>
              <button 
                onClick={() => navigate('/lead-gen')} 
                className="bg-purple-600 dark:bg-purple-500 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-purple-700 dark:hover:bg-purple-600 touch-target no-tap-highlight"
              >
                <Sparkles className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">{safeT('leads.aiLeadGeneration', 'AI Lead Generation')}</span>
                <span className="sm:hidden">AI Gen</span>
              </button>
            </div>
            
            {/* Expand/Collapse Button - Only on Desktop */}
            <button
              onClick={() => setMenuExpanded(!menuExpanded)}
              className="hidden sm:flex bg-gray-500 dark:bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight transition-transform"
              title={menuExpanded ? 'Collapse menu' : 'Expand menu'}
            >
              <MoreVertical className="h-4 w-4 flex-shrink-0" />
              <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${menuExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Create Lead Button - Always Visible */}
            <button 
              onClick={handleCreateLead} 
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5 flex-shrink-0" />
              <span>{safeT('leads.createLead', 'Create Lead')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar - Shows when leads are selected */}
      {selectedLeads.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedLeads.size} {selectedLeads.size === 1 ? 'lead' : 'leads'} selected
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowBulkTagManager(true)}
                disabled={loading}
                className="bg-blue-600 dark:bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium"
                title="Manage tags for selected leads"
              >
                <Tag className="h-4 w-4" />
                <span>Tag</span>
              </button>
              
              <button
                onClick={() => handleBulkDelete()}
                disabled={loading}
                className="bg-red-600 dark:bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium"
                title="Delete selected leads"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
              
              <button
                onClick={() => {
                  setSelectedLeads(new Set());
                  setSelectAll(false);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Clear selection"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Hidden by Default */}
      {showFilters && (
        <div className="space-y-4">
          {/* Legacy Simple Filters - Keep for backward compatibility */}
          <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
            hasSimpleFilters
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {hasSimpleFilters && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                  🔍 Simple Filters Active
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Clear All
                </button>
              </div>
            )}
            <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder={safeT('leads.searchPlaceholder', 'Search leads...')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{safeT('leads.allStatuses', 'All Statuses')}</option>
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{safeT('leads.allPriorities', 'All Priorities')}</option>
              {priorityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              onFocus={handleTagDropdownFocus}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              title="Filter by tag"
            >
              <option value="">{safeT('leads.allTags', 'All Tags')}</option>
              {availableTags && availableTags.length > 0 ? (
                availableTags.map((tag) => (
                  <option key={tag.id || tag.name} value={tag.name}>
                    {tag.name} ({tag.usageCount || 0})
                  </option>
                ))
              ) : tagsLoading ? (
                <option value="" disabled>Loading tags...</option>
              ) : null}
            </select>

            {!isFieldHidden('QualificationLevel') && (
              <select
                value={filters.qualificationLevel}
                onChange={(e) => handleFilterChange('qualificationLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{safeT('leads.allQualifications', 'All Qualifications')}</option>
                {qualificationLevelOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex justify-end items-center">
            <button
              type="submit"
              className="bg-gray-600 dark:bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base touch-target no-tap-highlight"
            >
              <Filter className="h-4 w-4" />
              <span>{safeT('common.filter', 'Filter')}</span>
            </button>
          </div>
        </form>
        </div>
        
          {/* EntityFilter Component */}
          <EntityFilter
            entityName="lead"
            filters={advancedFilters}
            onFiltersChange={(filters) => {
              console.log('🔍 Leads: Filters changed in EntityFilter:', filters);
              setAdvancedFilters(filters);
              setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
            }}
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Leads Table - List or Spreadsheet View */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : (
        <div>
          {viewMode === 'list' ? (
            <DynamicEntityTable
              entityName="lead"
              data={leads}
              loading={loading}
              onView={handleViewLead}
              onEdit={handleEditLead}
              onDelete={handleDeleteLead}
              customRender={customRender}
              onRowDoubleClick={handleViewLead}
              refs={leadRefs}
              onSelect={(id) => {
                // Sanitize ID to ensure it's a number
                const numId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
                if (isNaN(numId)) {
                  console.warn('Invalid ID received in onSelect:', id);
                  return;
                }
                setSelectedLeads(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(numId)) {
                    newSet.delete(numId);
                  } else {
                    newSet.add(numId);
                  }
                  return newSet;
                });
              }}
              selectedItems={selectedLeads}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
            />
          ) : (
            <SpreadsheetView
              entityName="lead"
              entityConfig={entityConfig}
              data={leads}
              columns={spreadsheetColumns}
              loading={loading}
              onView={handleViewLead}
              onEdit={handleEditLead}
              onDelete={handleDeleteLead}
              onCellSave={handleCellSave}
              onSelect={(id) => {
                // Sanitize ID to ensure it's a number
                const numId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
                if (isNaN(numId)) {
                  console.warn('Invalid ID received in onSelect:', id);
                  return;
                }
                setSelectedLeads(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(numId)) {
                    newSet.delete(numId);
                  } else {
                    newSet.add(numId);
                  }
                  return newSet;
                });
              }}
              selectedItems={selectedLeads}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              refs={leadRefs}
              getDisplayValue={getSpreadsheetCellValue}
              isFieldEditable={isFieldEditable}
              sortConfig={sortConfig}
              onSort={handleSort}
              i18n={i18n}
            />
          )}
          
          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between mt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {safeT('common.previous', 'Previous')}
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {safeT('common.next', 'Next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-xs sm:text-sm">{safeT('common.showing', 'Showing')}</span> <span className="text-xs sm:text-sm font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> <span className="text-xs sm:text-sm">{safeT('common.to', 'to')}</span> <span className="text-xs sm:text-sm font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> <span className="text-xs sm:text-sm">{safeT('common.of', 'of')}</span> <span className="text-xs sm:text-sm font-medium">{pagination.total}</span> <span className="text-xs sm:text-sm">{safeT('common.results', 'results')}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      {safeT('common.previous', 'Previous')}
                    </button>
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setPagination(prev => ({ ...prev, page }))}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagination.page === page
                              ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-200'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      {safeT('common.next', 'Next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {leads.length === 0 && !loading && (
        <div className="text-center py-8">
          <Target className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{safeT('leads.noLeads', 'No leads found')}</h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">{safeT('leads.noLeadsDescription', 'Get started by creating your first lead')}</p>
          <div className="mt-6">
            <button
              onClick={handleCreateLead}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              {safeT('leads.createLead', 'Create Lead')}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Tag Manager Modal */}
      <BulkTagManager
        isOpen={showBulkTagManager}
        onClose={() => setShowBulkTagManager(false)}
        entityType="Lead"
        entityIds={Array.from(selectedLeads)}
        entityCount={selectedLeads.size}
        onSuccess={handleBulkTagSuccess}
      />
      
      {/* Dependency Delete Dialog */}
      <DependencyDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, leadId: null, dependencies: null })}
        onConfirm={handleConfirmDeleteWithDependencies}
        entityName="lead"
        dependencies={deleteDialog.dependencies}
      />
    </div>
  );
};

export default Leads; 